import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { BaseLoader, ErrorMessage, SubmitButton } from '@retainerclub/common';
import { CREATE_PATIENT, UPDATE_PATIENT } from '@retainerclub/customer-api';
import { useAccount } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { initialValues, schema } from './helpers';

const DUPE_MESSAGE_REGEX = /already exists on this account/i;

function FamilyPatientModalWrapper({
  patient = null,
  handleCloseModal,
  children,
}) {
  const [mutationErrors, setMutationErrors] = useState(null);

  const { accountId, patients: existingPatients = [] } = useAccount();

  const [update, { error, loading }] = useMutation(UPDATE_PATIENT);
  const [create, { error: createError, loading: createLoading }] =
    useMutation(CREATE_PATIENT);

  const memoizedInitialValues = useMemo(
    () => initialValues({ record: { ...patient, accountId } }),
    [patient, accountId],
  );

  const {
    register,
    handleSubmit,
    formState: { isLoading, isSubmitting, isValid },
    formState,
    getValues,
    setValue,
    setError,
    trigger,
    control,
    ...rest
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: memoizedInitialValues,
    values: memoizedInitialValues,
    context: {
      existingPatients,
      currentPatientId: patient?.id || null,
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const firstNameValue = useWatch({ control, name: 'firstName' });

  // biome-ignore lint/correctness/useExhaustiveDependencies: firstNameValue is read as a change trigger so lastName is re-validated (clears stale dupe error when firstName changes)
  useEffect(() => {
    if (formState.touchedFields.lastName || formState.errors.lastName) {
      trigger('lastName');
    }
  }, [
    firstNameValue,
    trigger,
    formState.touchedFields.lastName,
    formState.errors.lastName,
  ]);

  async function submitRecord(values) {
    try {
      const params = {
        variables: {
          input: {
            input: { ...values },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      const mutate = patient ? update : create;
      const resultKey = patient ? 'updatePatient' : 'createPatient';

      const { data } = await mutate(params);
      const mErrors = data[resultKey].errorMessages;

      if (mErrors.length > 0) {
        const dupeError = mErrors.find((e) =>
          DUPE_MESSAGE_REGEX.test(e.message),
        );
        if (dupeError) {
          setError('lastName', {
            type: 'server',
            message: 'A patient with this name already exists on this account',
          });
        }
        setMutationErrors(mErrors);
      } else {
        setMutationErrors(null);
        handleCloseModal();
      }
    } catch (err) {
      console.error(err);
    }
  }

  if (loading || createLoading) {
    return <BaseLoader title="Patient" />;
  }

  return (
    <FormProvider
      {...rest}
      control={control}
      register={register}
      handleSubmit={handleSubmit}
      formState={formState}
      getValues={getValues}
      setValue={setValue}
      setError={setError}
    >
      <form onSubmit={handleSubmit(submitRecord)} noValidate>
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-6">
          <div className="col-span-6 grid grid-cols-1 gap-4 sm:grid-cols-6 md:col-span-6">
            {children}

            <div className="col-span-full">
              {error && (
                <div className="col-span-full mt-4">
                  <ErrorMessage error={error} />
                </div>
              )}

              {createError && (
                <div className="col-span-full mt-4">
                  <ErrorMessage error={createError} />
                </div>
              )}

              {mutationErrors && (
                <div className="col-span-full mt-4">
                  <ErrorMessage errorMessages={mutationErrors} />
                </div>
              )}
              <div className="col-span-full mt-4 flex items-center justify-end gap-x-4">
                <SubmitButton
                  isLoading={isLoading || loading || createLoading}
                  isSubmitting={isSubmitting}
                  isValid={isValid}
                  label={patient ? 'Update' : 'Create'}
                  full
                  notRounded
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

FamilyPatientModalWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  patient: PropTypes.object,
};

export default FamilyPatientModalWrapper;
